import {FormCardSection} from '@/components/ui/form-atoms/FormAtoms'
import {GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {useGetProjectPathologies} from '@/features/projects/queries/useGetProjectPathologies'
import {ProjectExtended} from '@/features/projects/types'
import {scaleTypeToLabel} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'

export const MedicalReportProjectDiagnosisCard = ({
    project
}: {
    project: Omit<ProjectExtended, 'missingGroups' | 'disabilityProfiles' | 'suspensions'>
}) => {
    const {t} = useTranslation()
    const {
        data: mainPathologies,
        isLoading: isMainPathologiesLoading,
        isError: isMainPathologiesError
    } = useGetProjectPathologies({
        urlParams: {projectId: project.id}
    })

    const evaluationScales = project.evaluationScales

    if (isMainPathologiesLoading) {
        return <MedicalReportProjectDiagnosisCardLoader />
    }

    if (isMainPathologiesError) {
        return <MedicalReportProjectDiagnosisCardError />
    }

    return (
        <FormCardSection columns={2} gap={2} padding={3}>
            {mainPathologies?.length && mainPathologies?.length > 0 ? (
                mainPathologies?.map((pathology, index) => (
                    <GridItem direction="column" gap={1.5} key={index}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('medical_records:diagnosis_code_pathology_n', {n: index + 1})}
                        </Text>
                        {/* ICD-9-CM and ICD-10-IM are independent: a pathology carries at least one
                            of the two, so each line is printed only when its own code is there. */}
                        {[pathology.pathology, pathology.icd10Pathology].map(
                            diagnosis =>
                                diagnosis && (
                                    <Text key={diagnosis.id} palette="neutral" shade="950" weight="500" size="textSm">
                                        {`${diagnosis.code} - ${diagnosis.description}`}
                                    </Text>
                                )
                        )}
                    </GridItem>
                ))
            ) : (
                <Text palette="neutral" shade="500" size="textSm">
                    {t('medical_records:no_pathologies')}
                </Text>
            )}

            {evaluationScales?.length && evaluationScales?.length > 0 ? (
                evaluationScales?.map((evaluationScale, index) => (
                    <GridItem direction="column" gap={1.5} key={index}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('medical_records:evaluation_scale_n', {n: index + 1})}
                        </Text>
                        <Text palette="neutral" shade="950" weight="500" size="textSm">
                            {t(scaleTypeToLabel[evaluationScale.type])} -{' '}
                            {t('projects:score_x', {count: evaluationScale?.totalScore})}
                        </Text>
                    </GridItem>
                ))
            ) : (
                <Text palette="neutral" shade="500" size="textSm">
                    {t('medical_records:no_evaluation_scales')}
                </Text>
            )}
        </FormCardSection>
    )
}

const MedicalReportProjectDiagnosisCardLoader = () => {
    return (
        <FormCardSection columns={2} gap={2} padding={3}>
            {Array.from({length: 4}).map((_, index) => (
                <GridItem direction="column" gap={1.5} key={index}>
                    <Skeleton height={20} width={150} />
                    <Skeleton height={20} width="100%" />
                </GridItem>
            ))}
        </FormCardSection>
    )
}

const MedicalReportProjectDiagnosisCardError = () => {
    const {t} = useTranslation()
    return (
        <FormCardSection columns={1} padding={3}>
            <Text palette="danger" shade="700" size="textSm">
                {t('errors:default')}
            </Text>
        </FormCardSection>
    )
}
